import type { ReactNode } from "react";
import { Link } from "wouter";
import { ChevronRight } from "lucide-react";

export interface SublineItem { icon?: ReactNode; content: ReactNode }

const CHIP = {
  category: "border-transparent bg-navy text-navy-foreground",
  good: "border-transparent bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
  warn: "border-transparent bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  gold: "border-transparent bg-primary/15 text-primary-text",
  plain: "bg-card text-muted-foreground",
} as const;

export function RecordChip({ tone = "plain", children }: { tone?: keyof typeof CHIP; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none ${CHIP[tone]}`}>
      {children}
    </span>
  );
}

/** Breadcrumb, name and chips, subline, copyable contact info, and the action bar. */
export function RecordHeader({ crumb, current, name, chips, subline, reach, actions }: {
  crumb: { label: string; href: string }; current: string; name: ReactNode; chips?: ReactNode;
  subline?: SublineItem[]; reach?: ReactNode; actions: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-muted-foreground">
        <Link href={crumb.href} className="hover:text-foreground">{crumb.label}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="truncate">{current}</span>
      </nav>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[520px] space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[22px] font-bold leading-tight tracking-tight">{name}</h1>
            {chips}
          </div>
          {subline && subline.length > 0 && (
            <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-[12.5px] text-muted-foreground">
              {subline.map((s, i) => (
                <span key={i} className="inline-flex min-w-0 max-w-full items-center gap-1.5 [&>svg]:h-3.5 [&>svg]:w-3.5">
                  {s.icon}
                  {typeof s.content === "string" ? <span className="max-w-[32rem] truncate" title={s.content}>{s.content}</span> : s.content}
                </span>
              ))}
            </div>
          )}
          {reach && <div className="flex flex-wrap gap-1.5 pt-0.5">{reach}</div>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">{actions}</div>
      </div>
    </div>
  );
}
